/**
 * ETHOS Premium Bottom Navigation
 * Floating pearl-glass pill with elevated amethyst Local action button.
 * Visual reference: soft ivory pill, centered Local oval with violet halo.
 */
import { useState, useRef, useEffect, useCallback } from "react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Compass, Heart, ChatCircle, MapPin, Sparkle,
  Gear, DotsThree, X, ShieldCheck, Crown, GameController,
} from "@phosphor-icons/react";
import { useAuth, apiClient } from "../App";

// ── Design tokens (day) ───────────────────────────────────────────────────────
const AMETHYST_DAY = "#6B4E8A";
const INACTIVE_DAY = "#8A8296";
const PILL_BG_DAY  = "linear-gradient(175deg, rgba(250,246,243,0.88) 0%, rgba(242,234,225,0.88) 100%)";
const PILL_SHADOW_DAY = "0 4px 20px rgba(107,78,138,0.12), 0 1px 6px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.90), 0 0 0 1px rgba(107,78,138,0.06)";

// ── Design tokens (night — Living Amethyst dark glass) ─────────────────────────
// Dark amethyst glass that reads as an elevated surface ON the night plum page —
// cohesive with the dark canvas, with bright amethyst icons + readable lavender labels.
const AMETHYST_NIGHT = "#C4A8F0";   // brighter amethyst — reads clearly on dark glass
const INACTIVE_NIGHT = "rgba(198,180,232,0.74)"; // light lavender — readable on dark
const PILL_BG_NIGHT  = "linear-gradient(175deg, rgba(48,34,72,0.92) 0%, rgba(36,25,56,0.94) 100%)";
const PILL_SHADOW_NIGHT = [
  "0 4px 30px rgba(0,0,0,0.46)",                 // deep ambient — floats above dark page
  "0 2px 10px rgba(0,0,0,0.34)",                 // grounded base shadow
  "inset 0 1px 0 rgba(255,255,255,0.10)",        // subtle top catch-light
  "inset 0 0 18px 2px rgba(160,100,255,0.10)",   // Living Amethyst inner bloom
  "0 0 0 1px rgba(168,136,216,0.22)",            // amethyst ring definition
].join(", ");

const LOCAL_GRADIENT = "linear-gradient(155deg, #C898FF 0%, #8044D4 46%, #5820A4 100%)";
const LOCAL_GLOW_ON = [
  "inset 0 2px 14px 2px rgba(220,190,255,0.22)",   // inner pearl crown bloom
  "inset 0 -1px 6px 1px rgba(75,20,135,0.18)",     // deep base undertone
  "0 0 0 1px rgba(175,115,255,0.16)",               // subtle ring definition
  "0 3px 16px rgba(107,78,138,0.28)",               // warm ambient spread
].join(", ");
const LOCAL_GLOW_OFF = [
  "inset 0 1px 8px 1px rgba(190,150,255,0.10)",    // resting inner bloom
  "0 0 0 1px rgba(140,82,255,0.07)",               // barely-there ring
  "0 2px 10px rgba(107,78,138,0.16)",              // ambient presence
].join(", ");

// Softer Local gradients for the INACTIVE state — keeps the orb elevated and
// recognisable but clearly secondary to the active tab. Pale lavender by day;
// muted (dimmer) amethyst by night so it never out-shines the dark glass pill.
const LOCAL_GRADIENT_SOFT_DAY   = "linear-gradient(155deg, #E2D2F7 0%, #C3A2EC 52%, #AC86DE 100%)";
const LOCAL_GRADIENT_SOFT_NIGHT = "linear-gradient(155deg, #6A5294 0%, #4E3A78 52%, #3A2A5C 100%)";

// ── Reusable nav item button ───────────────────────────────────────────────────
function NavBtn({ icon: Icon, label, testId, active, onClick, amethyst, inactive, badgeCount = 0, badgeDot = false, badgeColor }) {
  const showCount = badgeCount > 0;
  const showDot = !showCount && badgeDot;
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "3px",
        padding: "6px 4px 8px",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
        outline: "none",
        minHeight: "52px",
        transition: "opacity 0.15s ease",
        opacity: active ? 1 : 0.68,
      }}
    >
      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon
          size={22}
          weight={active ? "fill" : "regular"}
          style={{ color: active ? amethyst : inactive, transition: "color 0.15s ease" }}
        />
        {showCount && (
          <span
            data-testid={`${testId}-badge`}
            style={{
              position: "absolute", top: "-6px", left: "calc(50% + 5px)",
              minWidth: "16px", height: "16px", padding: "0 4px",
              borderRadius: "9999px", background: badgeColor, color: "#fff",
              fontSize: "9px", fontWeight: 700, lineHeight: "16px", textAlign: "center",
              boxShadow: "0 1px 3px rgba(0,0,0,0.25)", pointerEvents: "none",
            }}
          >
            {badgeCount > 9 ? "9+" : badgeCount}
          </span>
        )}
        {showDot && (
          <span
            data-testid={`${testId}-dot`}
            style={{
              position: "absolute", top: "-3px", left: "calc(50% + 7px)",
              width: "8px", height: "8px", borderRadius: "9999px",
              background: badgeColor, boxShadow: "0 1px 3px rgba(0,0,0,0.25)", pointerEvents: "none",
            }}
          />
        )}
      </div>
      <span style={{
        fontSize: "10px",
        fontWeight: active ? 650 : 400,
        color: active ? amethyst : inactive,
        lineHeight: 1.2,
        letterSpacing: "0.01em",
        transition: "color 0.15s ease",
      }}>
        {label}
      </span>
    </button>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
const BottomNav = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const [showMore, setShowMore] = useState(false);
  const moreRef   = useRef(null);

  // ── Night-mode detection — MutationObserver on html[data-night] ─────────────
  const [isNight, setIsNight] = useState(
    () => document.documentElement.dataset.night === "true"
  );
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => {
      setIsNight(el.dataset.night === "true");
    });
    obs.observe(el, { attributes: true, attributeFilter: ["data-night"] });
    return () => obs.disconnect();
  }, []);

  // ── Awareness indicators (Chats + Matches) — frontend-only, existing endpoints ──
  const [chatsUnread, setChatsUnread] = useState(0);
  const [dcCount, setDcCount] = useState(0);
  const [matchesHasUnread, setMatchesHasUnread] = useState(false);
  const [newMatches, setNewMatches] = useState(false);
  const lastAwarenessFetchRef = useRef(0);

  const fetchAwareness = useCallback(async () => {
    if (!user) return;
    let token = null;
    try { token = localStorage.getItem("token"); } catch {}
    if (!token) return;
    const now = Date.now();
    if (now - lastAwarenessFetchRef.current < 3000) return; // debounce rapid focus/visibility
    lastAwarenessFetchRef.current = now;
    const [chatsRes, dcRes, matchesRes] = await Promise.allSettled([
      apiClient.get("/chats"),
      apiClient.get("/direct-connect/requests"),
      apiClient.get("/matches"),
    ]);
    if (chatsRes.status === "fulfilled" && Array.isArray(chatsRes.value.data)) {
      setChatsUnread(chatsRes.value.data.reduce((s, c) => s + (c.unread_count || 0), 0));
    }
    if (dcRes.status === "fulfilled" && Array.isArray(dcRes.value.data)) {
      setDcCount(dcRes.value.data.length);
    }
    if (matchesRes.status === "fulfilled" && Array.isArray(matchesRes.value.data)) {
      const ms = matchesRes.value.data;
      setMatchesHasUnread(ms.some((m) => m.has_unread === true));
      let lastSeenMs = 0;
      try { const v = localStorage.getItem("ethos:lastSeenMatchesAt"); if (v) lastSeenMs = Date.parse(v) || 0; } catch {}
      setNewMatches(ms.some((m) => m.created_at && (Date.parse(m.created_at) || 0) > lastSeenMs));
    }
  }, [user]);

  // Poll conservatively (50s) only while visible; refresh on focus / visibility.
  useEffect(() => {
    if (!user) return;
    fetchAwareness();
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchAwareness();
    }, 50000);
    const onReturn = () => { if (document.visibilityState !== "hidden") fetchAwareness(); };
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [user, fetchAwareness]);

  // When the user is on /matches, mark matches as seen (clears the new-match dot).
  useEffect(() => {
    if (!location.pathname.startsWith("/matches")) return;
    const t = setTimeout(() => {
      try { localStorage.setItem("ethos:lastSeenMatchesAt", new Date().toISOString()); } catch {}
      setNewMatches(false);
    }, 1500);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // ── Adaptive design tokens ───────────────────────────────────────────────────
  const amethyst   = isNight ? AMETHYST_NIGHT   : AMETHYST_DAY;
  const inactive   = isNight ? INACTIVE_NIGHT   : INACTIVE_DAY;
  const pillBg     = isNight ? PILL_BG_NIGHT     : PILL_BG_DAY;
  const pillShadow = isNight ? PILL_SHADOW_NIGHT : PILL_SHADOW_DAY;
  const menuBg     = isNight ? "rgba(22,16,36,0.94)"   : "rgba(250,246,243,0.96)";
  const menuShadow = isNight
    ? ["0 10px 50px rgba(0,0,0,0.50)", "0 2px 12px rgba(0,0,0,0.28)", "0 0 0 1px rgba(107,78,138,0.24)"].join(", ")
    : ["0 10px 48px rgba(107,78,138,0.22)", "0 2px 10px rgba(0,0,0,0.08)", "0 0 0 1px rgba(107,78,138,0.10)"].join(", ");
  const menuItemColor       = isNight ? "rgba(210,192,242,0.88)" : "#6B5F78";
  const menuItemActiveColor = isNight ? "#C4A8F0"                : amethyst;

  const isChatThread = location.pathname.startsWith("/chat/");
  const isDocMode    = location.pathname.startsWith("/local/wisdom/print");

  // Close More drawer on outside click
  useEffect(() => {
    if (!showMore) return;
    const close = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setShowMore(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
    };
  }, [showMore]);

  // All hooks above this line
  if (isChatThread || isDocMode) return null;

  // ── Active state helpers ─────────────────────────────────────────────────────
  const isActive = (path) => {
    if (path === "/local")     return location.pathname.startsWith("/local");
    if (path === "/chats")     return location.pathname.startsWith("/chat");
    if (path === "/discovery") return location.pathname === "/discovery" || location.pathname === "/discover";
    return location.pathname === path;
  };

  const localActive    = isActive("/local");
  // Elevated orb is only meaningful on the main Local map itself — everywhere
  // else (My Neighbourhood, Local Wisdom, Steward, or any non-Local page) it
  // would just be clutter floating over unrelated content.
  const showLocalOrb   = location.pathname === "/local";
  const localSoftGradient = isNight ? LOCAL_GRADIENT_SOFT_NIGHT : LOCAL_GRADIENT_SOFT_DAY;
  const overflowActive = ["/offerings", "/settings", "/steward", "/games/green-pill"].some(p => location.pathname.startsWith(p));

  // Awareness badge values
  const chatsBadgeCount = chatsUnread + dcCount;
  const matchesDot = matchesHasUnread || newMatches;
  const badgeColor = isNight ? "#C4A8F0" : "#7C4DD6";

  // ── Overflow menu items ──────────────────────────────────────────────────────
  const overflowItems = [
    { path: "/store",     icon: Crown,   label: "Store & Upgrades", group: "top" },
    { path: "/offerings", icon: Sparkle, label: "Offerings",         group: "top" },
    { path: "/games/green-pill", icon: GameController, label: "GREEN PILL", group: "top" },
    { path: "/settings",  icon: Gear,    label: "Account",           group: "bottom" },
  ];

  return (
    <>
      {/* ── More overflow drawer ─────────────────────────────────────────────── */}
      {showMore && (
        <div
          ref={moreRef}
          style={{
            position: "fixed",
            bottom: "calc(max(env(safe-area-inset-bottom, 0px), 8px) + 84px)",
            right: "16px",
            zIndex: 120,
          }}
        >
          <div style={{
            background: menuBg,
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            borderRadius: "20px",
            boxShadow: menuShadow,
            overflow: "hidden",
            minWidth: "210px",
            animation: "moreMenuIn 0.16s ease-out",
          }}>
            {overflowItems.map((item, idx) => {
              const Icon = item.icon;
              const active = location.pathname === item.path;
              const prevGroup = idx > 0 ? overflowItems[idx - 1].group : null;
              const showDivider = prevGroup && prevGroup !== item.group;
              return (
                <React.Fragment key={item.path}>
                  {showDivider && (
                    <div style={{
                      height: 1,
                      background: isNight ? "rgba(255,255,255,0.08)" : "rgba(107,78,138,0.08)",
                      margin: "2px 0",
                    }} />
                  )}
                  <button
                    onClick={() => { navigate(item.path, item.path === "/games/green-pill" ? { state: { from: location.pathname } } : undefined); setShowMore(false); }}
                    data-testid={`nav-overflow-${item.label.toLowerCase().replace(/\s+/g,'-')}`}
                    style={{
                      width: "100%",
                      display: "flex", alignItems: "center", gap: "12px",
                      padding: "13px 20px",
                      background: "transparent", border: "none",
                      color: active ? menuItemActiveColor : menuItemColor,
                      fontSize: "14px",
                      fontWeight: active ? 600 : 400,
                      cursor: "pointer",
                      WebkitTapHighlightColor: "transparent",
                      transition: "background 0.12s ease",
                    }}
                  >
                    <Icon size={17} weight={active ? "fill" : "regular"} />
                    {item.label}
                  </button>
                </React.Fragment>
              );
            })}
            {(user?.is_steward || user?.is_admin || user?.is_founder) && (
              <>
                <div style={{
                  height: 1,
                  background: isNight ? "rgba(255,255,255,0.08)" : "rgba(107,78,138,0.08)",
                  margin: "2px 0",
                }} />
                <button
                  onClick={() => { navigate("/steward"); setShowMore(false); }}
                  data-testid="nav-steward"
                  style={{
                    width: "100%",
                    display: "flex", alignItems: "center", gap: "12px",
                    padding: "13px 20px",
                    background: "transparent", border: "none",
                    color: location.pathname.startsWith("/steward") ? menuItemActiveColor : menuItemColor,
                    fontSize: "14px",
                    fontWeight: location.pathname.startsWith("/steward") ? 600 : 400,
                    cursor: "pointer",
                    WebkitTapHighlightColor: "transparent",
                  }}
                >
                  <ShieldCheck size={17} weight={location.pathname.startsWith("/steward") ? "fill" : "regular"} />
                  Steward Mode
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Main floating nav ────────────────────────────────────────────────── */}
      <nav
        data-testid="bottom-nav"
        aria-label="Main navigation"
        style={{
          position: "fixed",
          bottom: 0, left: 0, right: 0,
          zIndex: 110,
          // Safe area + extra clearance so pill sits above home indicator
          paddingBottom: "max(env(safe-area-inset-bottom, 0px), 8px)",
          paddingLeft: "12px",
          paddingRight: "12px",
          // Allow Local button to protrude above pill without clipping
          paddingTop: "0px",
          pointerEvents: "none",
        }}
      >
        {/* Pill wrapper — overflow visible for elevated Local button */}
        <div
          className="bottom-nav-frame"
          style={{
            position: "relative",
            overflow: "visible",
            pointerEvents: "auto",
          }}
        >
          {/* ── Local featured button (elevated above pill) ─────────────────────
               Only shown on the main Local map — elsewhere it would just be
               visual clutter since the map is a scrollable/interactive canvas,
               not a second home screen. The nav-local-label spacer below still
               takes you to Local from everywhere else. ─────────────────────── */}
          {showLocalOrb && (
          <div
            style={{
              position: "absolute",
              // Centre horizontally over the pill
              left: "50%",
              transform: "translateX(-50%)",
              // Sit above the pill top edge — partial overlap for integrated look
              bottom: "calc(100% - 14px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "0px",
              zIndex: 2,
              pointerEvents: "auto",
            }}
          >
            <button
              onClick={() => navigate("/local")}
              data-testid="nav-local"
              aria-label="Local"
              style={{
                width: "54px",
                height: "62px",
                borderRadius: "27px",
                background: localActive ? LOCAL_GRADIENT : localSoftGradient,
                boxShadow: isNight && localActive
                  ? [
                      "inset 0 2px 16px 3px rgba(220,190,255,0.28)",
                      "inset 0 -1px 8px 1px rgba(75,20,135,0.24)",
                      "0 0 0 1px rgba(185,125,255,0.28)",
                      "0 4px 24px rgba(107,78,138,0.50)",
                      "0 0 38px 10px rgba(107,78,138,0.28)",
                    ].join(", ")
                  : localActive ? LOCAL_GLOW_ON : LOCAL_GLOW_OFF,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2.5px solid rgba(255,255,255,0.24)",
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
                outline: "none",
                transition: "box-shadow 0.35s ease, transform 0.12s ease",
                flexShrink: 0,
              }}
              className={localActive ? (isNight ? "amethyst-orb-breath-night" : "amethyst-orb-breath") : ""}
              onMouseDown={e => { e.currentTarget.style.transform = "scale(0.94)"; }}
              onMouseUp={e => { e.currentTarget.style.transform = "scale(1)"; }}
              onTouchStart={e => { e.currentTarget.style.transform = "scale(0.94)"; }}
              onTouchEnd={e => { e.currentTarget.style.transform = "scale(1)"; }}
            >
              {/* Pin icon + sparkle overlay */}
              <div style={{ position: "relative", width: 24, height: 24, opacity: localActive ? 1 : 0.9 }}>
                <MapPin size={24} weight="fill" style={{ color: "white", display: "block" }} />
                <Sparkle
                  size={9}
                  weight="fill"
                  style={{
                    color: "rgba(255,255,255,0.90)",
                    position: "absolute",
                    top: "18%",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                />
              </div>
            </button>
          </div>
          )}

          {/* ── Pearl glass pill — adaptive day/night ────────────────────────── */}
          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              background: pillBg,
              backdropFilter: isNight ? "blur(20px)" : "blur(14px)",
              WebkitBackdropFilter: isNight ? "blur(20px)" : "blur(14px)",
              borderRadius: "22px",
              minHeight: "58px",
              boxShadow: pillShadow,
              overflow: "hidden",
              position: "relative",
              zIndex: 1,
              transition: "background 0.6s ease, box-shadow 0.6s ease",
            }}
          >
            {/* Discover */}
            <NavBtn
              icon={Compass}
              label="Discover"
              testId="nav-discover"
              active={isActive("/discovery")}
              onClick={() => navigate("/discovery")}
              amethyst={amethyst}
              inactive={inactive}
            />

            {/* Matches */}
            <NavBtn
              icon={Heart}
              label="Matches"
              testId="nav-matches"
              active={isActive("/matches")}
              onClick={() => navigate("/matches")}
              amethyst={amethyst}
              inactive={inactive}
              badgeDot={matchesDot}
              badgeColor={badgeColor}
            />

            {/* Center spacer — reserved for the elevated Local button. When
                the orb is hidden (i.e. not on the main map) this becomes the
                tap target so Local stays reachable from every page. */}
            <div
              onClick={!showLocalOrb ? () => navigate("/local") : undefined}
              data-testid="nav-local-label"
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-end",
                paddingBottom: "12px",
                minWidth: 0,
                cursor: !showLocalOrb ? "pointer" : "default",
              }}
            >
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: localActive ? 650 : 400,
                  color: localActive ? amethyst : inactive,
                  lineHeight: 1.2,
                  letterSpacing: "0.01em",
                  transition: "color 0.15s ease",
                  userSelect: "none",
                  pointerEvents: "none",
                }}
              >
                Local
              </span>
            </div>

            {/* Chats */}
            <NavBtn
              icon={ChatCircle}
              label="Chats"
              testId="nav-chats"
              active={isActive("/chats")}
              onClick={() => navigate("/chats")}
              amethyst={amethyst}
              inactive={inactive}
              badgeCount={chatsBadgeCount}
              badgeColor={badgeColor}
            />

            {/* More */}
            <button
              onClick={() => setShowMore(v => !v)}
              data-testid="nav-more"
              aria-label="More options"
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "3px",
                padding: "6px 4px 8px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
                outline: "none",
                minHeight: "52px",
                opacity: overflowActive || showMore ? 1 : 0.68,
                transition: "opacity 0.15s ease",
              }}
            >
              {showMore ? (
                <X size={22} style={{ color: amethyst }} />
              ) : (
                <DotsThree
                  size={22}
                  weight={overflowActive ? "fill" : "bold"}
                  style={{ color: overflowActive ? amethyst : inactive, transition: "color 0.15s ease" }}
                />
              )}
              <span style={{
                fontSize: "10px",
                fontWeight: overflowActive || showMore ? 650 : 400,
                color: overflowActive || showMore ? amethyst : inactive,
                lineHeight: 1.2,
                letterSpacing: "0.01em",
                transition: "color 0.15s ease",
              }}>
                More
              </span>
            </button>
          </div>
        </div>
      </nav>
    </>
  );
};

export default BottomNav;
